import React, { useState, useCallback, useMemo } from 'react';
import { Card, Tab, Nav, Row, Col, Form, Button } from 'react-bootstrap';
import productivityApi from 'api/modules/productivityApi';
import ProductivityChart from './ProductivityChart';

const MAX_QUERY_DAYS = 31;

const TABS = [
  { key: 'picking', label: 'Picking' },
  { key: 'shipment', label: 'Shipment Packed' },
];

const INTERVAL_OPTIONS = [
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
];

const todayIST = () => {
  const now = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }),
  );
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toIST = dateStr => `${dateStr}T00:00:00+05:30`;
const toISTEnd = dateStr => `${dateStr}T23:59:59+05:30`;

const Productivity = () => {
  const today = todayIST();

  const [draftStart, setDraftStart] = useState(today);
  const [draftEnd, setDraftEnd] = useState(today);
  const [draftInterval, setDraftInterval] = useState('hour');

  const [appliedStart, setAppliedStart] = useState(today);
  const [appliedEnd, setAppliedEnd] = useState(today);
  const [appliedInterval, setAppliedInterval] = useState('hour');

  const [activeTab, setActiveTab] = useState('picking');
  const [visitedTabs, setVisitedTabs] = useState({ picking: true });

  const rangeError = useMemo(() => {
    if (!draftStart || !draftEnd) return null;
    const s = new Date(draftStart);
    const e = new Date(draftEnd);
    if (s > e) return 'Start date must be before end date.';
    const diffDays = (e - s) / (1000 * 60 * 60 * 24);
    if (diffDays > MAX_QUERY_DAYS)
      return `Maximum query range is ${MAX_QUERY_DAYS} days.`;
    return null;
  }, [draftStart, draftEnd]);

  const isDirty =
    draftStart !== appliedStart ||
    draftEnd !== appliedEnd ||
    draftInterval !== appliedInterval;

  const handleApply = () => {
    if (rangeError) return;
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
    setAppliedInterval(draftInterval);
    setVisitedTabs(prev => ({ ...prev, [activeTab]: true }));
  };

  const handleTabSelect = key => {
    setActiveTab(key);
    if (!visitedTabs[key]) {
      setVisitedTabs(prev => ({ ...prev, [key]: true }));
    }
  };

  const fetchPicking = useCallback(
    params =>
      productivityApi.getPicking({
        start: toIST(params.start),
        end: toISTEnd(params.end),
        interval: params.interval,
      }),
    [],
  );

  const fetchShipment = useCallback(
    params =>
      productivityApi.getShipmentCompletion({
        start: toIST(params.start),
        end: toISTEnd(params.end),
        interval: params.interval,
      }),
    [],
  );

  return (
    <Card>
      <Card.Header className="bg-body-tertiary">
        <Row className="align-items-center g-2">
          <Col>
            <h5 className="mb-0">Productivity</h5>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-2">
            <Form.Control
              type="date"
              size="sm"
              value={draftStart}
              max={draftEnd}
              onChange={e => setDraftStart(e.target.value)}
            />
            <span className="text-600">~</span>
            <Form.Control
              type="date"
              size="sm"
              value={draftEnd}
              min={draftStart}
              onChange={e => setDraftEnd(e.target.value)}
            />
            <Form.Select
              size="sm"
              value={draftInterval}
              onChange={e => setDraftInterval(e.target.value)}
              style={{ width: 'auto' }}
            >
              {INTERVAL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Button
              size="sm"
              variant="primary"
              disabled={!!rangeError || !isDirty}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {rangeError ? (
          <div className="text-center text-danger py-4">{rangeError}</div>
        ) : (
          <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
            <Nav variant="tabs" className="mb-3">
              {TABS.map(tab => (
                <Nav.Item key={tab.key}>
                  <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="picking">
                {visitedTabs.picking && (
                  <ProductivityChart
                    fetchFn={fetchPicking}
                    start={appliedStart}
                    end={appliedEnd}
                    interval={appliedInterval}
                  />
                )}
              </Tab.Pane>
              <Tab.Pane eventKey="shipment">
                {visitedTabs.shipment && (
                  <ProductivityChart
                    fetchFn={fetchShipment}
                    start={appliedStart}
                    end={appliedEnd}
                    interval={appliedInterval}
                  />
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        )}
      </Card.Body>
    </Card>
  );
};

export default Productivity;
